import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';

interface DashboardStats {
  servers: {
    total: number;
    active: number;
    inactive: number;
  };
  users: {
    total: number;
    active: number;
    inactive: number;
    admins: number;
    managers: number;
  };
  companies: {
    total: number;
    active: number;
    inactive: number;
  };
}

const CrudDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string>('user');
  const [cleanupStatus, setCleanupStatus] = useState<string>('');

  useEffect(() => {
    fetchDashboardStats();
    // Get user role from auth context or API
    // This is a placeholder - you'd get this from your auth system
    setUserRole('admin'); // For demo purposes
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      
      // Fetch data from all endpoints
      const [serversResponse, usersResponse, companiesResponse] = await Promise.all([
        apiService.getAllServers(1000).catch(() => [] as any[]),
        apiService.getAllUsers(1000).catch(() => [] as any[]),
        apiService.getCompanies(0, 1000).catch(() => [] as any[])
      ]);

      // Process servers data
      const servers = Array.isArray(serversResponse) ? serversResponse : [];
      const serverStats = {
        total: servers.length,
        active: servers.filter((s: any) => s.is_active).length,
        inactive: servers.filter((s: any) => !s.is_active).length,
      };

      // Process users data
      const users = Array.isArray(usersResponse) ? usersResponse : [];
      const userStats = {
        total: users.length,
        active: users.filter((u: any) => u.is_active).length,
        inactive: users.filter((u: any) => !u.is_active).length,
        admins: users.filter((u: any) => u.access === 'admin').length,
        managers: users.filter((u: any) => u.access === 'manager').length,
      };

      // Process companies data
      const companies = Array.isArray(companiesResponse) ? companiesResponse : [];
      const companyStats = {
        total: companies.length,
        active: companies.filter((c: any) => c.is_active).length,
        inactive: companies.filter((c: any) => !c.is_active).length,
      };

      setStats({
        servers: serverStats,
        users: userStats,
        companies: companyStats,
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const canManageServers = userRole === 'admin';
  const canManageUsers = userRole === 'admin' || userRole === 'manager';
  const canManageCompanies = userRole === 'admin' || userRole === 'manager';

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Management Dashboard</h1>
        <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>
          Role: <span className={`badge ${
            userRole === 'admin' ? 'badge-danger' :
            userRole === 'manager' ? 'badge-warning' :
            'badge-info'
          }`}>
            {userRole}
          </span>
        </div>
      </div>

      {/* Quick Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
        gap: '1.5rem', 
        marginBottom: '2rem' 
      }}>
        {/* Servers Card */}
        <div className="card">
          <div className="card-header">
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              🖥️ Servers
              {canManageServers && (
                <Link to="/servers" className="btn btn-sm btn-primary" style={{ marginLeft: 'auto' }}>
                  Manage
                </Link>
              )}
            </h3>
          </div>
          <div className="card-body">
            {stats ? (
              <div className="info-grid">
                <div className="info-item">
                  <label>Total Servers</label>
                  <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                    {stats.servers.total}
                  </span>
                </div>
                <div className="info-item">
                  <label>Active</label>
                  <span className="badge badge-success">
                    {stats.servers.active}
                  </span>
                </div>
                <div className="info-item">
                  <label>Inactive</label>
                  <span className="badge badge-danger">
                    {stats.servers.inactive}
                  </span>
                </div>
              </div>
            ) : (
              <p>No data available</p>
            )}
          </div>
        </div>

        {/* Users Card */}
        <div className="card">
          <div className="card-header">
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              👥 Users
              {canManageUsers && (
                <Link to="/users" className="btn btn-sm btn-primary" style={{ marginLeft: 'auto' }}>
                  Manage
                </Link>
              )}
            </h3>
          </div>
          <div className="card-body">
            {stats ? (
              <div className="info-grid">
                <div className="info-item">
                  <label>Total Users</label>
                  <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                    {stats.users.total}
                  </span>
                </div>
                <div className="info-item">
                  <label>Active</label>
                  <span className="badge badge-success">
                    {stats.users.active}
                  </span>
                </div>
                <div className="info-item">
                  <label>Admins</label>
                  <span className="badge badge-danger">
                    {stats.users.admins}
                  </span>
                </div>
                <div className="info-item">
                  <label>Managers</label>
                  <span className="badge badge-warning">
                    {stats.users.managers}
                  </span>
                </div>
              </div>
            ) : (
              <p>No data available</p>
            )}
          </div>
        </div>

        {/* Companies Card */}
        <div className="card">
          <div className="card-header">
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              🏢 Companies
              {canManageCompanies && (
                <Link to="/companies" className="btn btn-sm btn-primary" style={{ marginLeft: 'auto' }}>
                  Manage
                </Link>
              )}
            </h3>
          </div>
          <div className="card-body">
            {stats ? (
              <div className="info-grid">
                <div className="info-item">
                  <label>Total Companies</label>
                  <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                    {stats.companies.total}
                  </span>
                </div>
                <div className="info-item">
                  <label>Active</label>
                  <span className="badge badge-success">
                    {stats.companies.active}
                  </span>
                </div>
                <div className="info-item">
                  <label>Inactive</label>
                  <span className="badge badge-danger">
                    {stats.companies.inactive}
                  </span>
                </div>
              </div>
            ) : (
              <p>No data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <div className="card-header">
          <h3 style={{ margin: 0 }}>Quick Actions</h3>
        </div>
        <div className="card-body">
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '1rem' 
          }}>
            {canManageServers && (
              <Link to="/servers" className="btn btn-secondary" style={{ textAlign: 'center' }}>
                🖥️ Manage Servers
              </Link>
            )}
            {canManageUsers && (
              <Link to="/users" className="btn btn-secondary" style={{ textAlign: 'center' }}>
                👥 Manage Users
              </Link>
            )}
            {canManageCompanies && (
              <Link to="/companies" className="btn btn-secondary" style={{ textAlign: 'center' }}>
                🏢 Manage Companies
              </Link>
            )}
            <button 
              onClick={fetchDashboardStats} 
              className="btn btn-primary"
              style={{ textAlign: 'center' }}
            >
              🔄 Refresh Data
            </button>
            {userRole === 'admin' && (
              <button
                onClick={async () => {
                  try {
                    setCleanupStatus('Running cleanup...');
                    const res = await apiService.customers.cleanupSupervisedControllers();
                    setCleanupStatus(`Cleanup completed: deactivated customers ${res.deactivated_customers}, deleted contracts ${res.deleted_contracts}`);
                  } catch (e: any) {
                    const msg = e?.response?.data?.detail || e?.message || 'Unknown error';
                    setCleanupStatus(`Cleanup failed: ${msg}`);
                  }
                }}
                className="btn btn-danger"
                style={{ textAlign: 'center' }}
              >
                🧹 Clean Up Supervised Controller Data
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Role-based Information */}
      <div className="card">
        <div className="card-header">
          <h3 style={{ margin: 0 }}>Access Information</h3>
        </div>
        <div className="card-body">
          {cleanupStatus && (
            <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
              {cleanupStatus}
            </div>
          )}
          <div className="alert alert-info">
            <h4 style={{ margin: '0 0 0.5rem 0' }}>Your Role: {userRole}</h4>
            {userRole === 'admin' && (
              <p style={{ margin: 0 }}>
                As an <strong>Administrator</strong>, you have full access to manage all servers, users, and companies. 
                You can create, edit, and delete any resource in the system.
              </p>
            )}
            {userRole === 'manager' && (
              <p style={{ margin: 0 }}>
                As a <strong>Manager</strong>, you can manage users and companies, but your access to servers is limited 
                to those assigned to you. You can create and edit users within your assigned scope.
              </p>
            )}
            {userRole === 'user' && (
              <p style={{ margin: 0 }}>
                As a <strong>User</strong>, you have read-only access to view information about your assigned 
                server and company. You can update your own profile information.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity (Placeholder) */}
      <div className="card">
        <div className="card-header">
          <h3 style={{ margin: 0 }}>Recent Activity</h3>
        </div>
        <div className="card-body">
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
            <p>Activity tracking will be implemented in a future update.</p>
            <p>This section will show recent changes to servers, users, and companies.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrudDashboard;